import type { Editor } from "@tiptap/react";

export function parseInlineStyleMap(style: string): Record<string, string> {
  const styleMap: Record<string, string> = {};
  style
    .split(";")
    .map((s) => s.trim())
    .filter(Boolean)
    .forEach((declaration) => {
      const [prop, ...rest] = declaration.split(":");
      if (!prop || rest.length === 0) return;
      styleMap[prop.trim().toLowerCase()] = rest.join(":").trim();
    });
  return styleMap;
}

export function inlineStyleMapToString(styleMap: Record<string, string>): string {
  return Object.entries(styleMap)
    .map(([key, value]) => `${key}: ${value}`)
    .join("; ");
}

function styleDeclaresUnderline(style: string | null | undefined): boolean {
  if (!style) return false;
  const value = parseInlineStyleMap(style)["text-decoration"] ?? "";
  return value.includes("underline");
}

/**
 * True when the selection has any form of underline: the TipTap underline mark,
 * `text-decoration` on a textStyle span, or on a paragraph's inline style.
 */
export function selectionHasUnderline(editor: Editor): boolean {
  if (editor.isActive("underline")) {
    return true;
  }

  const { from, to } = editor.state.selection;
  let found = false;

  editor.state.doc.nodesBetween(from, to, (node) => {
    if (found) return false;

    const nodeType = node.type?.name;
    if (!nodeType) return true;

    if (nodeType === "paragraph") {
      if (styleDeclaresUnderline(node.attrs?.style as string | undefined)) {
        found = true;
        return false;
      }
    }

    if (node.isText || nodeType === "dynamicMention") {
      for (const mark of node.marks ?? []) {
        if (
          mark.type?.name === "textStyle" &&
          styleDeclaresUnderline(mark.attrs?.style as string | undefined)
        ) {
          found = true;
          return false;
        }
      }
    }

    return true;
  });

  return found;
}

/**
 * Toggle underline on the current selection. When removing, clears the underline
 * mark and any `text-decoration: underline` on paragraph or textStyle nodes.
 */
export function toggleReportTextUnderline(editor: Editor): boolean {
  if (!editor) return false;

  const { from, to } = editor.state.selection;
  const shouldRemove = selectionHasUnderline(editor);

  if (!shouldRemove) {
    return editor.chain().focus().setUnderline().run();
  }

  return editor
    .chain()
    .focus()
    .command(({ tr, state, dispatch }) => {
      const underlineMark = state.schema.marks.underline;
      const textStyleMark = state.schema.marks.textStyle;

      if (underlineMark) {
        tr.removeMark(from, to, underlineMark);
      }

      tr.doc.nodesBetween(from, to, (node, pos) => {
        const nodeType = node.type?.name;
        if (!nodeType) return true;

        if (nodeType === "paragraph") {
          const currentStyle = (node.attrs?.style as string | undefined) ?? "";
          if (!styleDeclaresUnderline(currentStyle)) {
            return true;
          }

          const styleMap = parseInlineStyleMap(currentStyle);
          delete styleMap["text-decoration"];
          const nextStyle = inlineStyleMapToString(styleMap);
          tr.setNodeMarkup(pos, undefined, {
            ...node.attrs,
            style: nextStyle || null,
          });
          return true;
        }

        if (!node.isText && nodeType !== "dynamicMention") {
          return true;
        }

        const existingTextStyle = node.marks?.find(
          (mark) => mark.type?.name === "textStyle",
        );
        if (
          !existingTextStyle ||
          !styleDeclaresUnderline(existingTextStyle.attrs?.style as string)
        ) {
          return true;
        }

        const styleMap = parseInlineStyleMap(
          (existingTextStyle.attrs?.style as string) ?? "",
        );
        delete styleMap["text-decoration"];
        const nextStyle = inlineStyleMapToString(styleMap);
        const start = Math.max(pos, from);
        const end = Math.min(pos + node.nodeSize, to);

        tr.removeMark(start, end, existingTextStyle);

        if (nextStyle && textStyleMark) {
          tr.addMark(
            start,
            end,
            textStyleMark.create({
              class: existingTextStyle.attrs?.class ?? null,
              style: nextStyle,
            }),
          );
        }
      });

      if (dispatch) dispatch(tr);
      return true;
    })
    .run();
}
